// services/api.js - API Client for Backend Communication

const API_BASE = import.meta.env.VITE_API_URL || '';

/**
 * Make an API request
 */
async function request(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return { success: true };
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error.message);
    throw error;
  }
}

// ============================================
// FORM ENDPOINTS (Public)
// ============================================

/**
 * Get form data by short code
 */
export async function getFormData(shortCode) {
  return request(`/form/${shortCode}`);
}

/**
 * Get weather for form location
 */
export async function getWeather(shortCode, lat, lon, address) {
  const params = new URLSearchParams();
  if (lat && lon) {
    params.set('lat', lat);
    params.set('lon', lon);
  }
  if (address) {
    params.set('address', address);
  }
  const queryString = params.toString();
  return request(`/form/${shortCode}/weather${queryString ? `?${queryString}` : ''}`);
}

/**
 * Get favorite products for a method
 */
export async function getFavorites(shortCode, method) {
  return request(`/form/${shortCode}/favorites/${method}`);
}

/**
 * Submit application record
 */
export async function submitApplication(shortCode, formData, photos = []) {
  const data = new FormData();

  // Add JSON data
  data.append('data', JSON.stringify(formData));

  // Add photos
  photos.forEach((photo, index) => {
    data.append('photos', photo.file, photo.file.name);
  });

  const url = `${API_BASE}/form/${shortCode}/submit`;

  const response = await fetch(url, {
    method: 'POST',
    body: data,
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Submission failed');
  }

  return result;
}

/**
 * Submit multiple application records (multi-product)
 */
export async function submitMultipleApplications(shortCode, applications, photos = []) {
  const data = new FormData();

  // Add applications array as JSON
  data.append('applications', JSON.stringify(applications));

  // Add photos
  photos.forEach((photo, index) => {
    data.append('photos', photo.file, photo.file.name);
  });

  const url = `${API_BASE}/form/${shortCode}/submit-multiple`;

  const response = await fetch(url, {
    method: 'POST',
    body: data,
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Submission failed');
  }

  return result;
}

/**
 * Quick add product
 */
export async function addProduct(shortCode, productData) {
  return request(`/form/${shortCode}/product`, {
    method: 'POST',
    body: JSON.stringify(productData),
  });
}

/**
 * Quick add equipment
 */
export async function addEquipment(shortCode, name) {
  return request(`/form/${shortCode}/equipment`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

/**
 * Toggle product favorite
 */
export async function toggleFavorite(shortCode, productName, method) {
  return request(`/form/${shortCode}/favorite`, {
    method: 'POST',
    body: JSON.stringify({ productName, method }),
  });
}

/**
 * Refresh cached data
 */
export async function refreshCache(shortCode) {
  return request(`/form/${shortCode}/refresh`, {
    method: 'POST',
  });
}

// ============================================
// STANDALONE FORM ENDPOINTS (No Jobber Job)
// ============================================

/**
 * Get standalone form data by tenant ID
 */
export async function getStandaloneFormData(tenantId) {
  return request(`/standalone/${tenantId}`);
}

/**
 * Get weather for standalone form
 */
export async function getStandaloneWeather(tenantId, lat, lon, address) {
  const params = new URLSearchParams();
  if (lat && lon) {
    params.set('lat', lat);
    params.set('lon', lon);
  }
  if (address) {
    params.set('address', address);
  }
  const queryString = params.toString();
  return request(`/standalone/${tenantId}/weather${queryString ? `?${queryString}` : ''}`);
}

/**
 * Search Jobber customers (clients) for autofill on the standalone form.
 * Returns { customers, jobberConnected } and never throws on lookup failure
 * (the backend degrades to an empty list).
 */
export async function searchCustomers(tenantId, searchTerm) {
  const params = new URLSearchParams({ search: searchTerm });
  return request(`/standalone/${tenantId}/customers?${params.toString()}`);
}

/**
 * Google Places address autocomplete for the service-address field.
 * Returns { suggestions } and degrades to an empty list when Places isn't configured.
 */
export async function autocompleteAddress(tenantId, query, sessionToken) {
  const params = new URLSearchParams({ q: query });
  if (sessionToken) params.set('sessiontoken', sessionToken);
  return request(`/standalone/${tenantId}/address-autocomplete?${params.toString()}`);
}

/**
 * Resolve a selected place to a full formatted address (with postal code).
 */
export async function getPlaceDetails(tenantId, placeId, sessionToken) {
  const params = new URLSearchParams({ placeId });
  if (sessionToken) params.set('sessiontoken', sessionToken);
  return request(`/standalone/${tenantId}/place-details?${params.toString()}`);
}

/**
 * Submit standalone application record
 */
export async function submitStandaloneApplication(tenantId, formData, photos = []) {
  const data = new FormData();
  
  // Add JSON data
  data.append('data', JSON.stringify(formData));
  
  // Add photos
  photos.forEach((photo, index) => {
    data.append('photos', photo.file, photo.file.name);
  });
  
  const url = `${API_BASE}/standalone/${tenantId}/submit`;
  
  const response = await fetch(url, {
    method: 'POST',
    body: data,
  });
  
  const result = await response.json();
  
  if (!response.ok) {
    throw new Error(result.error || 'Submission failed');
  }
  
  return result;
}

/**
 * Submit multiple standalone application records (multi-product)
 */
export async function submitStandaloneApplications(tenantId, applications, photos = []) {
  const data = new FormData();

  // Add applications array as JSON
  data.append('applications', JSON.stringify(applications));

  // Add photos
  photos.forEach((photo, index) => {
    data.append('photos', photo.file, photo.file.name);
  });

  const url = `${API_BASE}/standalone/${tenantId}/submit-multi`;

  const response = await fetch(url, {
    method: 'POST',
    body: data,
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Submission failed');
  }

  return result;
}

/**
 * Quick add product (standalone)
 */
export async function addStandaloneProduct(tenantId, productData) {
  return request(`/standalone/${tenantId}/product`, {
    method: 'POST',
    body: JSON.stringify(productData),
  });
}

/**
 * Quick add equipment (standalone)
 */
export async function addStandaloneEquipment(tenantId, name) {
  return request(`/standalone/${tenantId}/equipment`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

// ============================================
// ADMIN ENDPOINTS
// ============================================

/**
 * Get OAuth connection status
 */
export async function getConnectionStatus(tenantId) {
  return request(`/oauth/status?tenantId=${tenantId}`);
}

/**
 * Disconnect from Jobber
 */
export async function disconnect(tenantId) {
  return request('/oauth/disconnect', {
    method: 'POST',
    body: JSON.stringify({ tenantId }),
  });
}

/**
 * Disconnect Google integration (keeps sheet/folder for reconnection)
 */
export async function disconnectGoogle(tenantId) {
  return request('/oauth/google/disconnect', {
    method: 'POST',
    body: JSON.stringify({ tenantId }),
  });
}

/**
 * Get tenant details
 */
export async function getTenant(tenantId) {
  return request(`/api/tenant/${tenantId}`);
}

/**
 * Get all tenants
 */
export async function getTenants() {
  return request('/api/tenants');
}

/**
 * Get form links for tenant
 */
export async function getFormLinks(tenantId, options = {}) {
  const params = new URLSearchParams();
  if (options.status) params.set('status', options.status);
  if (options.limit) params.set('limit', options.limit);
  const queryString = params.toString();
  return request(`/api/tenant/${tenantId}/form-links${queryString ? `?${queryString}` : ''}`);
}

/**
 * Get form link stats
 */
export async function getFormLinkStats(tenantId) {
  return request(`/api/tenant/${tenantId}/form-link-stats`);
}

/**
 * Create manual form link
 */
export async function createFormLink(tenantId, data) {
  return request(`/api/tenant/${tenantId}/form-link`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Revoke form link
 */
export async function revokeFormLink(shortCode) {
  return request(`/api/form-link/${shortCode}`, {
    method: 'DELETE',
  });
}

/**
 * Get products for tenant
 */
export async function getProducts(tenantId) {
  return request(`/api/tenant/${tenantId}/products`);
}

/**
 * Get applicators for tenant
 */
export async function getApplicators(tenantId) {
  return request(`/api/tenant/${tenantId}/applicators`);
}

/**
 * Get equipment for tenant
 */
export async function getEquipmentList(tenantId) {
  return request(`/api/tenant/${tenantId}/equipment`);
}

/**
 * Add product (admin)
 */
export async function addProductAdmin(tenantId, productData) {
  return request(`/api/tenant/${tenantId}/product`, {
    method: 'POST',
    body: JSON.stringify(productData),
  });
}

/**
 * Add equipment (admin)
 */
export async function addEquipmentAdmin(tenantId, name) {
  return request(`/api/tenant/${tenantId}/equipment`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

/**
 * Refresh cache (admin)
 */
export async function refreshCacheAdmin(tenantId) {
  return request(`/api/tenant/${tenantId}/refresh-cache`, {
    method: 'POST',
  });
}

/**
 * Get webhook events (debugging)
 */
export async function getWebhookEvents(tenantId, limit = 20) {
  return request(`/api/tenant/${tenantId}/webhook-events?limit=${limit}`);
}

// ============================================
// LOCAL STORAGE HELPERS (Offline Backup)
// ============================================

const PENDING_SUBMISSIONS_KEY = 'pendingPesticideSubmissions';

/**
 * Save submission to local storage (for offline backup)
 */
export function savePendingSubmission(shortCode, formData) {
  const pending = getPendingSubmissions();
  pending.push({
    shortCode,
    formData,
    timestamp: new Date().toISOString(),
    id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  });
  localStorage.setItem(PENDING_SUBMISSIONS_KEY, JSON.stringify(pending));
}

/**
 * Get all pending submissions
 */
export function getPendingSubmissions() {
  try {
    const stored = localStorage.getItem(PENDING_SUBMISSIONS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Remove a pending submission
 */
export function removePendingSubmission(id) {
  const pending = getPendingSubmissions();
  const filtered = pending.filter(s => s.id !== id);
  localStorage.setItem(PENDING_SUBMISSIONS_KEY, JSON.stringify(filtered));
}

/**
 * Clear all pending submissions
 */
export function clearPendingSubmissions() {
  localStorage.removeItem(PENDING_SUBMISSIONS_KEY);
}

export default {
  getFormData,
  getWeather,
  getFavorites,
  submitApplication,
  submitMultipleApplications,
  addProduct,
  addEquipment,
  toggleFavorite,
  refreshCache,
  getConnectionStatus,
  disconnect,
  disconnectGoogle,
  getTenant,
  getTenants,
  getFormLinks,
  getFormLinkStats,
  createFormLink,
  revokeFormLink,
  getProducts,
  getApplicators,
  getEquipmentList,
  addProductAdmin,
  addEquipmentAdmin,
  refreshCacheAdmin,
  getWebhookEvents,
  savePendingSubmission,
  getPendingSubmissions,
  removePendingSubmission,
  clearPendingSubmissions,
};
